import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { veiculosApi } from '../../api/endpoints'
import { Card, CardBody, CardHeader } from '../../components/ui/Card'
import { LoadingState } from '../../components/ui/EmptyState'
import { OrigemVencimento, STATUS_MANUTENCAO_LABEL, UNIDADE_ENERGIA_LABEL } from '../../lib/enums'
import { Badge } from '../../components/ui/Badge'
import { formatDate, formatMoney } from '../../lib/utils'
import { TendenciaBadge } from './ConsumoSecao'
import { RendeuTexto } from './AbastecimentosPage'
import { formatKm, inicioDoMes, numeroBr, toneStatus, unidadeConsumo } from './veiculoUtils'

const verMais = (to: string, rotulo: string) => <Link to={to} className="text-13 text-muted underline underline-offset-2 hover:text-primary">{rotulo}</Link>

function Vazio({ children }: { children: string }) { return <p className="text-13 text-muted">{children}</p> }

/** Consumo do detalhe: média ponderada, mediana e últimos 5 com tendência. */
export function CardConsumo({ veiculoId }: { veiculoId: string }) {
  const { data, isLoading } = useQuery({ queryKey: ['veiculos', veiculoId, 'consumo', '', ''], queryFn: () => veiculosApi.consumo(veiculoId) })
  return (
    <div data-tutorial="garagem.consumo"><Card>
      <CardHeader title="Consumo" icon="ri-speed-line" actions={verMais(`/veiculos/${veiculoId}/consumo`, 'Ver detalhes')} />
      <CardBody className="space-y-4">
        {isLoading ? <LoadingState /> : !data || data.energias.every((e) => e.mediaPonderada == null) ? (
          <Vazio>Ainda sem trecho fechado. Com dois abastecimentos de tanque cheio (ou reserva) o consumo aparece aqui.</Vazio>
        ) : data.energias.map((e) => {
          const un = unidadeConsumo(e.unidade)
          return (
            <div key={e.unidade} className="space-y-2">
              {data.energias.length > 1 && <p className="text-2xs uppercase text-muted">{UNIDADE_ENERGIA_LABEL[e.unidade] === 'kWh' ? 'Eletricidade' : 'Combustível'}</p>}
              <div className="grid grid-cols-3 gap-3 text-13">
                <div><p className="text-2xs uppercase text-muted">Média</p><p className="mt-0.5 text-lg font-semibold text-primary">{e.mediaPonderada != null ? numeroBr(e.mediaPonderada, 1) : '—'}</p><p className="text-2xs text-muted">{un}</p></div>
                <div><p className="text-2xs uppercase text-muted">Mediana</p><p className="mt-0.5 text-lg font-semibold text-white">{e.mediana != null ? numeroBr(e.mediana, 1) : '—'}</p><p className="text-2xs text-muted">{un}</p></div>
                <div><p className="text-2xs uppercase text-muted">Últimos 5</p><p className="mt-0.5 text-lg font-semibold text-white">{e.mediaUltimos5 != null ? numeroBr(e.mediaUltimos5, 1) : '—'}</p><p className="text-2xs text-muted">{un}</p></div>
              </div>
              <TendenciaBadge t={e.tendencia} variacao={e.variacaoPercentual} />
            </div>
          )
        })}
        {data?.custoPorKm != null && <p className="border-t border-border/70 pt-3 text-13 text-muted">Custo por km: <span className="font-medium text-white">{formatMoney(data.custoPorKm)}</span></p>}
      </CardBody>
    </Card></div>
  )
}

/** Último abastecimento: o que rendeu, ou (em andamento) km desde então e autonomia estimada. */
export function CardUltimo({ veiculoId, base }: { veiculoId: string; base: string }) {
  const { data, isLoading } = useQuery({ queryKey: ['veiculos', veiculoId, 'consumo', '', ''], queryFn: () => veiculosApi.consumo(veiculoId) })
  const u = data?.ultimo
  return (
    <Card>
      <CardHeader title="Último abastecimento" icon="ri-gas-station-line" actions={verMais(`${base}/abastecimentos`, 'Ver todos')} />
      <CardBody className="space-y-3">
        {isLoading ? <LoadingState /> : !u ? <Vazio>Nenhum abastecimento registrado ainda.</Vazio> : (
          <>
            <div className="grid grid-cols-3 gap-3 text-13">
              <div><p className="text-2xs uppercase text-muted">Data</p><p className="mt-0.5 text-white">{formatDate(u.data)}</p></div>
              <div><p className="text-2xs uppercase text-muted">Quantidade</p><p className="mt-0.5 text-white">{numeroBr(u.quantidade, 1)} {UNIDADE_ENERGIA_LABEL[u.unidade]}</p></div>
              <div><p className="text-2xs uppercase text-muted">Valor</p><p className="mt-0.5 text-white">{formatMoney(u.valorTotal)}</p></div>
            </div>
            <div className="border-t border-border/70 pt-3 text-13">
              <p className="text-2xs uppercase text-muted">Rendeu</p>
              <p className="mt-0.5"><RendeuTexto r={u.rendimento} unidade={u.unidade} /></p>
            </div>
            {!u.rendimento.calculado && (
              <div className="rounded bg-surface2 px-3 py-2.5 text-13">
                <p className="text-white">{formatKm(u.kmDesdeAbastecimento)} rodados desde então</p>
                {u.autonomiaEstimadaKm != null && (
                  <p className="text-xs text-muted">Autonomia estimada: {formatKm(Math.round(u.autonomiaEstimadaKm))}{u.kmRestantesEstimados != null ? ` · faltam cerca de ${formatKm(Math.max(0, Math.round(u.kmRestantesEstimados)))}` : ''}</p>
                )}
              </div>
            )}
          </>
        )}
      </CardBody>
    </Card>
  )
}

/** Custo do mês: combustível (dos abastecimentos) mais as despesas do veículo. */
export function CardCusto({ veiculoId, comCombustivel, comManutencao = false, base }: { veiculoId: string; comCombustivel: boolean; comManutencao?: boolean; base: string }) {
  const de = inicioDoMes()
  const { data: consumo, isLoading: l1 } = useQuery({
    queryKey: ['veiculos', veiculoId, 'consumo', de, ''], queryFn: () => veiculosApi.consumo(veiculoId, { de }), enabled: comCombustivel,
  })
  const { data: despesas = [], isLoading: l2 } = useQuery({
    queryKey: ['veiculos', veiculoId, 'despesas', de, ''], queryFn: () => veiculosApi.despesas.list(veiculoId, { de }),
  })
  const { data: manutencoes = [], isLoading: l3 } = useQuery({
    queryKey: ['veiculos', veiculoId, 'manutencoes', de, ''], queryFn: () => veiculosApi.manutencoes.list(veiculoId, { de }), enabled: comManutencao,
  })
  const combustivel = consumo?.gastoTotal ?? 0
  const manut = manutencoes.reduce((s, m) => s + m.valor, 0)
  const outras = despesas.reduce((s, d) => s + d.valor, 0)
  return (
    <Card>
      <CardHeader title="Custo do mês" icon="ri-wallet-3-line" actions={verMais(`${base}/despesas`, 'Despesas')} />
      <CardBody>
        {l1 || l2 || l3 ? <LoadingState /> : (
          <div className="space-y-3">
            <p className="text-2xl font-semibold text-primary">{formatMoney(combustivel + manut + outras)}</p>
            <dl className="space-y-1.5 text-13">
              {comCombustivel && <div className="flex justify-between"><dt className="text-muted">Combustível e recarga</dt><dd className="text-white">{formatMoney(combustivel)}</dd></div>}
              {comManutencao && <div className="flex justify-between"><dt className="text-muted">Manutenção ({manutencoes.length})</dt><dd className="text-white">{formatMoney(manut)}</dd></div>}
              <div className="flex justify-between"><dt className="text-muted">Despesas ({despesas.length})</dt><dd className="text-white">{formatMoney(outras)}</dd></div>
            </dl>
          </div>
        )}
      </CardBody>
    </Card>
  )
}

/** Próximos vencimentos: manutenção e despesas com data, do mais urgente ao menos. Clicar leva à tela de origem. */
export function CardVencimentos({ veiculoId, base }: { veiculoId: string; base: string }) {
  const { data = [], isLoading, isError } = useQuery({ queryKey: ['veiculos', veiculoId, 'vencimentos'], queryFn: () => veiculosApi.vencimentos(veiculoId) })
  return (
    <Card>
      <CardHeader title="Próximos vencimentos" icon="ri-calendar-event-line" actions={verMais(`${base}/manutencoes`, 'Manutenções')} />
      <CardBody>
        {isLoading ? <LoadingState /> : isError ? <Vazio>Não foi possível carregar os vencimentos.</Vazio> : data.length === 0 ? (
          <Vazio>Nada a vencer. Cadastre itens de manutenção ou o vencimento de IPVA e seguro nas despesas.</Vazio>
        ) : (
          <ul className="space-y-2">
            {data.slice(0, 6).map((x) => (
              <li key={`${x.origem}-${x.id}`}>
                <Link to={x.origem === OrigemVencimento.Manutencao ? `${base}/manutencoes` : `${base}/despesas`} className="flex items-center justify-between gap-3 rounded bg-surface2 px-3 py-2.5 hover:bg-surface3">
                  <span className="min-w-0">
                    <span className="block truncate text-13 text-white">{x.titulo}</span>
                    <span className="block text-xs text-muted">{x.texto}</span>
                  </span>
                  <Badge variant={toneStatus(x.status)} className="flex-shrink-0">{STATUS_MANUTENCAO_LABEL[x.status]}</Badge>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  )
}
